import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Flame, Crown, Share2, Link2, Download } from "lucide-react";

export default function WeeklyWinners() {
  const [data, setData] = useState(null);
  const shareUrl = `${API}/leaderboard/winners/page`;

  useEffect(() => {
    axios
      .get(`${API}/leaderboard/winners`)
      .then(({ data }) => setData(data))
      .catch((err) => console.debug("Weekly winners unavailable:", err));
  }, []);

  if (!data || !(data.winners || []).length) return null;

  const medals = ["text-[#F59E0B]", "text-gray-300", "text-[#CD7F32]"];

  const nativeShare = async () => {
    const text = `This week's top win streaks on TradeInsights AI 🏆`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Weekly Winners", text, url: shareUrl });
      } catch (err) {
        console.debug("Share dismissed:", err);
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success("Winners link copied!");
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <Card className="glass-effect border border-[#F59E0B]/20 mb-6" data-testid="weekly-winners-card">
        <CardContent className="p-5">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <div className="flex items-center gap-2">
                <Crown className="w-4 h-4 text-[#F59E0B]" />
                <h2 className="text-sm font-bold text-white uppercase tracking-wider">Weekly winners</h2>
              </div>
              <p className="text-[11px] text-gray-500 mt-0.5">Week {data.week_key} · top win streaks, refreshed every Monday</p>
            </div>
            <div className="flex items-center gap-2">
              <Button data-testid="winners-share-btn" size="sm" onClick={nativeShare} className="bg-[#F59E0B] text-black hover:bg-[#F59E0B]/90 font-bold h-8 text-xs">
                <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share
              </Button>
              <Button
                data-testid="winners-copy-btn"
                size="sm"
                variant="outline"
                className="border-white/15 text-gray-300 hover:bg-white/10 hover:text-white h-8 text-xs"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(shareUrl);
                    toast.success("Winners link copied!");
                  } catch {
                    toast.error("Couldn't access the clipboard");
                  }
                }}
              >
                <Link2 className="w-3.5 h-3.5 mr-1.5" /> Copy link
              </Button>
              <Button
                data-testid="winners-download-btn"
                size="sm"
                variant="outline"
                className="border-white/15 text-gray-300 hover:bg-white/10 hover:text-white h-8 text-xs"
                onClick={() => window.open(`${API}/leaderboard/winners/og.png`, "_blank")}
              >
                <Download className="w-3.5 h-3.5 mr-1.5" /> Image
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="weekly-winners-list">
            {data.winners.slice(0, 3).map((w, i) => (
              <div key={`${w.display_name}-${i}`} className="rounded-lg border border-white/5 bg-white/[0.02] px-4 py-3">
                <div className="flex items-center gap-2 mb-1">
                  <Crown className={`w-4 h-4 ${medals[i]}`} />
                  <span className="text-sm font-bold text-white truncate">{w.display_name}</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                  <Flame className="w-3.5 h-3.5 text-[#00FFA3]" />
                  <span className="text-[#00FFA3] font-bold">{w.win_streak} win streak</span>
                  <span className="text-gray-500">· {w.win_rate}% WR</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
